import { useMemo, useReducer } from 'react';

import { arrayMove } from '@dnd-kit/sortable';
import { enableAllPlugins, produce } from 'immer';
import _ from 'lodash';

import { buildProjectTableConfiguration } from 'components/projectsDashboard/components/ProjectsTable';

enableAllPlugins();

export default function useProjectTableConfiguration({
  dashboardConfigurableFields = [],
  isApprovalConfigurable,
  isFolderConfigurable,
  isReviewConfigurable,
  isRiskAssessmentConfigurable,
  isSignatureConfigurable,
  singularRAMSName,
  tableColumnConfigurations = [],
  tableSortConfiguration = [],
}) {
  const defaultConfigurationArgs = {
    dashboardConfigurableFields,
    isApprovalConfigurable,
    isFolderConfigurable,
    isReviewConfigurable,
    isRiskAssessmentConfigurable,
    isSignatureConfigurable,
    singularRAMSName,
  };

  const defaultConfiguration = useMemo(
    () => buildProjectTableConfiguration(defaultConfigurationArgs),
    [Object.values(defaultConfigurationArgs)],
  );

  const seededConfigurationArgs = {
    dashboardConfigurableFields,
    isApprovalConfigurable,
    isFolderConfigurable,
    isReviewConfigurable,
    isRiskAssessmentConfigurable,
    isSignatureConfigurable,
    singularRAMSName,
    tableColumnConfigurations,
    tableSortConfiguration,
  };

  const seededConfiguration = useMemo(
    () => buildProjectTableConfiguration(seededConfigurationArgs),
    [Object.values(seededConfigurationArgs)],
  );

  const [projectTableConfiguration, dispatch] = useReducer(
    reducer,
    seededConfiguration,
  );

  const handleProjectTableColumnOrderChange = ({ active, over }) => {
    dispatch({
      type: 'CHANGE_COLUMN_ORDER',
      payload: { active, over },
    });
  };

  const handleProjectTableColumnSizingChange = (columnSizing) => {
    dispatch({
      type: 'CHANGE_COLUMN_SIZING',
      payload: { columnSizing },
    });
  };

  const handleProjectTableColumnSortChange = ({
    columnId,
    direction,
    isFutureDirectionShownOnHover,
    isUnsorted,
  }) => {
    dispatch({
      type: 'CHANGE_COLUMN_SORT',
      payload: {
        columnId,
        direction,
        isFutureDirectionShownOnHover,
        isUnsorted,
      },
    });
  };

  const handleProjectTableColumnVisibilityChange = ({ columnId, value }) => {
    dispatch({
      type: 'CHANGE_COLUMN_VISIBILITY',
      payload: { columnId, value },
    });
  };

  const handleProjectTableConfigurationsReset = () => {
    dispatch({ type: 'RESET', payload: { defaultConfiguration } });
  };

  return {
    handleProjectTableColumnOrderChange,
    handleProjectTableColumnSizingChange,
    handleProjectTableColumnSortChange,
    handleProjectTableColumnVisibilityChange,
    handleProjectTableConfigurationsReset,
    projectTableConfiguration,
  };
}

function reducer(state, action) {
  return produce(state, (draftState) => {
    switch (action.type) {
      case 'CHANGE_COLUMN_ORDER': {
        const { active, over } = action.payload;
        const oldIndex = draftState.columnOrder.indexOf(active.id);
        const newIndex = draftState.columnOrder.indexOf(over.id);
        draftState.columnOrder = arrayMove(
          draftState.columnOrder,
          oldIndex,
          newIndex,
        );
        draftState.isReset = false;
        break;
      }
      case 'CHANGE_COLUMN_SIZING': {
        const { columnSizing } = action.payload;
        // tanstack table returns either an object (initially) or a function (subsequently)
        // requires additional work when state managed by reducer rather than state setter
        draftState.columnSizing =
          _.isFunction(columnSizing) ?
            columnSizing(draftState.columnSizing)
          : columnSizing;
        draftState.isReset = false;
        break;
      }
      case 'CHANGE_COLUMN_SORT': {
        draftState.columnSort = action.payload;
        draftState.isReset = false;
        break;
      }
      case 'CHANGE_COLUMN_VISIBILITY': {
        const { columnId, value } = action.payload;
        draftState.columnVisibility = {
          ...draftState.columnVisibility,
          [columnId]: value,
        };
        draftState.isReset = false;
        break;
      }
      case 'RESET': {
        const { defaultConfiguration } = action.payload;
        return { ...defaultConfiguration, isReset: true };
      }
    }
  });
}
